import { useState, useEffect, Fragment } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

import {
  Grid,
  Box,
  Button,
  Modal,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Divider,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

// Utils
import messages from '../../static/messages';

// Services
import appointmentsServices from '../../services/appointmentsServices';

// External variables
import { modal_style } from '../../static/styles';
import { button } from '../../static/theme-styles';

const TIME_BLOCK_TYPE_ID = 36; // Time Block Appointment Type

const base_time_block = {
  startAt: null,
  finishAt: null,
  date: null,
  reason: '',
  user: '',
  typeId: TIME_BLOCK_TYPE_ID,
};

const required_fields = ['user', 'date', 'startAt', 'finishAt'];

const appointment_times = [
  { time12: '07:00 AM', time24: '07:00' },
  { time12: '07:30 AM', time24: '07:30' },
  { time12: '08:00 AM', time24: '08:00' },
  { time12: '08:30 AM', time24: '08:30' },
  { time12: '09:00 AM', time24: '09:00' },
  { time12: '09:30 AM', time24: '09:30' },
  { time12: '10:00 AM', time24: '10:00' },
  { time12: '10:30 AM', time24: '10:30' },
  { time12: '11:00 AM', time24: '11:00' },
  { time12: '11:30 AM', time24: '11:30' },
  { time12: '12:00 PM', time24: '12:00' },
  { time12: '12:30 PM', time24: '12:30' },
  { time12: '01:00 PM', time24: '13:00' },
  { time12: '01:30 PM', time24: '13:30' },
  { time12: '02:00 PM', time24: '14:00' },
  { time12: '02:30 PM', time24: '14:30' },
  { time12: '03:00 PM', time24: '15:00' },
  { time12: '03:30 PM', time24: '15:30' },
  { time12: '04:00 PM', time24: '16:00' },
  { time12: '04:30 PM', time24: '16:30' },
  { time12: '05:00 PM', time24: '17:00' },
  { time12: '05:30 PM', time24: '17:30' },
  { time12: '06:00 PM', time24: '18:00' },
  { time12: '06:30 PM', time24: '18:30' },
  { time12: '07:00 PM', time24: '19:00' },
  { time12: '07:30 PM', time24: '19:30' },
  { time12: '08:00 PM', time24: '20:00' },
  { time12: '08:30 PM', time24: '20:30' },
  { time12: '09:00 PM', time24: '21:00' },
  { time12: '09:30 PM', time24: '21:30' },
  { time12: '10:00 PM', time24: '22:00' },
];

const TimeBlockModal = ({ 
  open, 
  close, 
  technicians, 
  getAppointments, 
  getQueue, 
  date, 
  preselectedTechId = null,
  blockInfo = null  // Existing time block info for view/delete mode
}) => {
  const [errors, setErrors] = useState({});
  const [timeBlock, setTimeBlock] = useState({
    ...base_time_block,
    date: date ? moment(date).format('MM/DD/YYYY') : null,
    user: preselectedTechId || '',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [fullBlockInfo, setFullBlockInfo] = useState(null);

  moment.tz.setDefault('America/Los_Angeles');
  const confirm = useConfirm();

  // Check if we're in view mode (existing block) or create mode
  const isViewMode = !!blockInfo;

  // Load full appointment details (including createdBy) when opening in view mode
  useEffect(() => {
    if (open && blockInfo?.id) {
      const loadFullDetails = async () => {
        try {
          const response = await appointmentsServices.getAppointmentById(blockInfo.id);
          if (response.data) {
            setFullBlockInfo(response.data);
          }
        } catch (error) {
          console.log('Error loading time block details:', error);
        }
      };
      loadFullDetails();
    } else {
      setFullBlockInfo(null);
    }
  }, [open, blockInfo]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (blockInfo) {
        // View mode - don't need to set form fields
      } else {
        // Create mode
        setTimeBlock({
          ...base_time_block,
          date: date ? moment(date).format('MM/DD/YYYY') : null,
          user: preselectedTechId || '',
        });
        resetErrors();
      }
    }
  }, [open, date, preselectedTechId, blockInfo]);

  // Action functions
  const resetErrors = () => {
    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      })),
    );
  };

  // Handler functions
  const handleChange = (event) => {
    const key = event.target.name;
    const val = event.target.value;

    setTimeBlock((prevState) => ({
      ...prevState,
      [key]: val,
      isEdited: true,
    }));
  };

  const handleClose = () => {
    if (!isViewMode && timeBlock.isEdited) {
      confirm({
        title: messages.confirm_message.clear,
        description: messages.confirm_message.clear_description,
      })
        .then(() => close())
        .catch(() => toast.info(messages.cancel_message));
    } else {
      close();
    }
  };

  const handleValidate = () => {
    let hasErrors = false;
    
    required_fields.forEach((field) => {
      if (timeBlock[field] === '' || timeBlock[field] === null || !timeBlock[field]) {
        setErrors((prevState) => ({
          ...prevState,
          [field]: true,
        }));
        hasErrors = true;
      } else {
        setErrors((prevState) => ({
          ...prevState,
          [field]: false,
        }));
      }
    });

    // Validate that end time is after start time
    if (timeBlock.startAt && timeBlock.finishAt) {
      const startMinutes = parseInt(timeBlock.startAt.split(':')[0]) * 60 + parseInt(timeBlock.startAt.split(':')[1]);
      const endMinutes = parseInt(timeBlock.finishAt.split(':')[0]) * 60 + parseInt(timeBlock.finishAt.split(':')[1]);
      
      if (endMinutes <= startMinutes) {
        toast.error('End time must be after start time');
        return;
      }

      const firstSlotMinutes = 7 * 60;   // 07:00
      const lastSlotMinutes = 22 * 60;   // 22:00
      if (startMinutes <= firstSlotMinutes && endMinutes >= lastSlotMinutes) {
        toast.error('Cannot block the entire day. Use Day Off for full-day unavailability, or select a shorter time range.');
        return;
      }

      const spanHours = (endMinutes - startMinutes) / 60;
      if (spanHours > 10) {
        toast.error('Time block cannot exceed 10 hours. Use Day Off for longer unavailability.');
        return;
      }
    }

    if (!hasErrors) {
      createTimeBlock();
    }
  };

  // Delete time block
  const handleDelete = async () => {
    if (!blockInfo?.id) return;

    confirm({
      title: 'Remove Time Block',
      description: 'Are you sure you want to remove this time block?',
    })
      .then(async () => {
        setIsDeleting(true);
        try {
          const response = await appointmentsServices.remove(blockInfo.id);
          if (response.status === 200) {
            toast.success('Time Block removed successfully!');
            close();
            if (getAppointments && date) {
              getAppointments(date.split('T')[0]);
            }
            if (getQueue) {
              getQueue();
            }
          }
        } catch (error) {
          toast.error('Failed to remove time block');
          console.error(error);
        } finally {
          setIsDeleting(false);
        }
      })
      .catch(() => {});
  };

  // Async functions
  const createTimeBlock = async () => {
    // Create datetime in PST timezone, then convert to UTC for storage
    // This ensures dates stay correct across timezone conversions
    const start = moment.tz(
      `${timeBlock.date} ${timeBlock.startAt}:00`,
      'MM/DD/YYYY HH:mm:ss',
      'America/Los_Angeles'
    );

    const end = moment.tz(
      `${timeBlock.date} ${timeBlock.finishAt}:00`,
      'MM/DD/YYYY HH:mm:ss',
      'America/Los_Angeles'
    );

    const data = {
      type: {
        id: timeBlock.typeId,
      },
      user: {
        id: timeBlock.user,
      },
      startTime: start.utc().format(),
      endTime: end.utc().format(),
      ...(timeBlock.reason && {
        notes: [
          {
            note: timeBlock.reason,
          },
        ],
      }),
    };

    try {
      const response = await appointmentsServices.create(data);

      if (response.status === 201) {
        toast.success('Time blocked successfully');
        close();
        getAppointments(date.split('T')[0]);
        getQueue();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // View Mode - Show existing time block info
  if (isViewMode) {
    // Use fullBlockInfo (loaded via API with createdBy) or fall back to blockInfo from list
    const info = fullBlockInfo || blockInfo;

    return (
      <Modal open={open} onClose={handleClose}>
        <Box sx={modal_style}>
          <Typography variant="h5" sx={{ color: '#f57c00', mb: 2 }}>
            🚫 Time Block Info
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Technician:</strong> {info?.user?.fullName || `${info?.user?.firstName || ''} ${info?.user?.lastName || ''}`.trim() || '—'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Date:</strong> {info?.startTime ? moment(info.startTime).format('MM/DD/YYYY') : '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Start Time:</strong> {info?.startTime ? moment(info.startTime).format('h:mm A') : '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>End Time:</strong> {info?.endTime ? moment(info.endTime).format('h:mm A') : '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Reason:</strong> {info?.notes?.[0]?.note || 'Not specified'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Created By:</strong>{' '}
              {info?.createdBy
                ? `${info.createdBy.fullName || ''}${info.createdAt ? ` — ${moment(info.createdAt).format('MM/DD/YYYY h:mm A')}` : ''}`
                : '—'}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              onClick={handleDelete}
              disabled={isDeleting}
              sx={{ 
                bgcolor: '#d32f2f',
                '&:hover': { bgcolor: '#b71c1c' }
              }}
            >
              {isDeleting ? 'Removing...' : 'Remove Time Block'}
            </Button>
            <Button variant="contained" sx={button('secondary')} onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  // Create Mode - Show form to create new time block
  return (
    <Modal open={open} onClose={close}>
      <Box sx={modal_style}>
        <Typography variant="h5" sx={{ color: '#f57c00' }}>
          🚫 Block Technician Time
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Block specific hours for a technician (meeting, training, etc.)
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="user" error={errors.user}>
                Technician *
              </InputLabel>
              <Select
                required
                labelId="user"
                name="user"
                value={timeBlock?.user}
                label="Technician *"
                onChange={handleChange}
                error={errors.user}
              >
                {technicians?.map((tech) => (
                  <MenuItem key={tech.tech_id} value={tech.tech_id}>
                    {tech.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.user && (
                <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="Date *"
                inputFormat="MM/DD/YYYY"
                value={timeBlock.date}
                onChange={(newDate) =>
                  setTimeBlock((prevState) => ({
                    ...prevState,
                    // Don't use moment.utc() - it shifts the date by timezone offset
                    date: moment(newDate.$d).format('MM/DD/YYYY'),
                    isEdited: true,
                  }))
                }
                renderInput={(params) => (
                  <Fragment>
                    <TextField {...params} name="date" fullWidth error={errors.date} />
                    {errors.date && (
                      <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
                    )}
                  </Fragment>
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="startAt" error={errors.startAt}>
                Start Time *
              </InputLabel>
              <Select
                labelId="startAt"
                name="startAt"
                value={timeBlock?.startAt || ''}
                label="Start Time *"
                onChange={handleChange}
                error={errors.startAt}
              >
                {appointment_times.map((time, index) => (
                  <MenuItem key={index} value={time.time24}>
                    {time.time12}
                  </MenuItem>
                ))}
              </Select>
              {errors.startAt && (
                <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="finishAt" error={errors.finishAt}>
                End Time *
              </InputLabel>
              <Select
                labelId="finishAt"
                name="finishAt"
                value={timeBlock?.finishAt || ''}
                label="End Time *"
                onChange={handleChange}
                error={errors.finishAt}
              >
                {appointment_times.map((time, index) => (
                  <MenuItem key={index} value={time.time24}>
                    {time.time12}
                  </MenuItem>
                ))}
              </Select>
              {errors.finishAt && (
                <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="reason"
              label="Reason (optional)"
              placeholder="Meeting, training, lunch, etc."
              multiline
              rows={2}
              value={timeBlock.reason}
              fullWidth
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Button 
              variant="contained" 
              onClick={handleValidate} 
              sx={{ 
                ...button('primary', 'secondary'),
                bgcolor: '#f57c00',
                '&:hover': { bgcolor: '#ef6c00' }
              }}
            >
              Block Time
            </Button>
            <Button variant="contained" sx={{ ...button('secondary'), ml: 2 }} onClick={handleClose}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default TimeBlockModal;
